import {useState, useRef, useEffect} from 'react'
import {pencilIcon, locationIcon, lock, rightArrow} from '@img'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import FlagSelectWithNumber from '@/customComponents/uiComponents/global/FlagSelectWithNumber.jsx'
import OtpInput, {formatOtpTimer} from '@/customComponents/uiComponents/global/OtpInput.jsx'
import {RadioButton} from '@/customComponents/uiComponents/RadioButton.jsx'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'
import PlaceAutocomplete from '@/customComponents/uiComponents/global/PlaceAutocomplete.jsx'
import {Listbox, ListboxButton, ListboxOptions, ListboxOption} from '@headlessui/react'
import Tooltip from '@/customComponents/uiComponents/global/Tooltip.jsx'
import {useUser} from '@/hooks/useUser'
import {identityMissing} from '@/utils/identityGate'
import IdentityIncompleteNotice from '@/customComponents/uiComponents/global/IdentityIncompleteNotice.jsx'
import {useUserEmails, useAddEmail, useRequestEmailOtp, useVerifyEmailOtp, useSetDefaultEmail, useUserPhones, useAddPhone, useRequestPhoneOtp, useVerifyPhoneOtp, useSetDefaultPhone, useUserAddresses, useAddAddress} from '@/hooks/useUserContacts'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {userService} from '@/services/userService'
import {useAuth} from '@/context/AuthContext'
import DeleteAccountFlow from '@/customComponents/accountDeletion/DeleteAccountFlow.jsx'
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {parsePhoneNumberFromString} from 'libphonenumber-js'
import {visibleEmail} from '@/utils/placeholderEmail'

const languages = ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Arabic', 'Chinese', 'Hindi']

export default function EditProfile() {
    const {data: user, isLoading: userLoading} = useUser()
    // What the booking gate still wants from this account, straight off the user payload.
    // Recomputed from the cached user, so verifying or adding the missing contact makes the
    // warning disappear on the next /account refetch without any local bookkeeping.
    const profileIdentityMissing = identityMissing(user)
    // The name is locked once it is real. It is only editable while the gate says it is
    // missing — a desk-created guest carries a placeholder ("Guest Guest") that only they can
    // replace, and a locked field here left the notice permanently unfixable.
    const nameMissing = profileIdentityMissing.includes('name')
    // The booking email is the account's DEFAULT one. While the gate says it is missing, the
    // address being added must become the default — otherwise the placeholder stays default
    // and the gate never clears — so "Set as default" is forced on and locked.
    const emailMustBeDefault = profileIdentityMissing.includes('email')
    const {login, logout} = useAuth()
    const queryClient = useQueryClient()

    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [phone, setPhone] = useState('')
    const [whatsapp, setWhatsapp] = useState('')
    const [sameWhatsApp, setSameWhatsApp] = useState(false)
    const [language] = useState('English')
    const [selectedLanguage, setSelectedLanguage] = useState(language)
    const [activeModal, setActiveModal] = useState(null)
    const [profilePhoto, setProfilePhoto] = useState('')
    const [previewPhoto, setPreviewPhoto] = useState(null)
    const [selectedFile, setSelectedFile] = useState(null)
    const fileInputRef = useRef(null)
    const [cancelConfirm, setCancelConfirm] = useState(false)

    // Password modal state
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [showCurrentPass, setShowCurrentPass] = useState(false)
    const [showNewPass, setShowNewPass] = useState(false)
    const [showConfirmPass, setShowConfirmPass] = useState(false)
    const [passwordError, setPasswordError] = useState('')

    // Email add/verify state
    const [emailDraft, setEmailDraft] = useState('')
    const [emailOtp, setEmailOtp] = useState(['', '', '', '', '', ''])
    const [emailStep, setEmailStep] = useState('input') // 'input' | 'otp'
    const [emailMakeDefault, setEmailMakeDefault] = useState(false)
    const [emailError, setEmailError] = useState('')
    const [emailResendTimer, setEmailResendTimer] = useState(0)
    const [emailResendMessage, setEmailResendMessage] = useState('')

    // Phone add/verify state
    const [phoneDraft, setPhoneDraft] = useState('')
    const [phoneOtp, setPhoneOtp] = useState(['', '', '', '', '', ''])
    const [phoneStep, setPhoneStep] = useState('input') // 'input' | 'otp'
    const [phoneMakeDefault, setPhoneMakeDefault] = useState(false)
    const [phoneError, setPhoneError] = useState('')
    const [phoneResendTimer, setPhoneResendTimer] = useState(0)
    const [phoneResendMessage, setPhoneResendMessage] = useState('')

    // Address add state
    const [addressPlace, setAddressPlace] = useState(null)
    const [addressLabel, setAddressLabel] = useState('')
    const [addressError, setAddressError] = useState('')

    // Email/phone/address list hooks
    const {data: emails = [], isLoading: emailsLoading} = useUserEmails()
    const {data: phones = [], isLoading: phonesLoading} = useUserPhones()
    const {data: addresses = []} = useUserAddresses()
    const addAddressMutation = useAddAddress()
    const addEmailMutation = useAddEmail()
    const requestEmailOtpMutation = useRequestEmailOtp()
    const verifyEmailOtpMutation = useVerifyEmailOtp()
    const setDefaultEmailMutation = useSetDefaultEmail()
    const addPhoneMutation = useAddPhone()
    const requestPhoneOtpMutation = useRequestPhoneOtp()
    const verifyPhoneOtpMutation = useVerifyPhoneOtp()
    const setDefaultPhoneMutation = useSetDefaultPhone()

    // Email resend timer countdown
    useEffect(() => {
        if (emailResendTimer <= 0) return
        const t = setInterval(() => {
            setEmailResendTimer((prev) => (prev <= 1 ? 0 : prev - 1))
        }, 1000)
        return () => clearInterval(t)
    }, [emailResendTimer])

    // Phone resend timer countdown
    useEffect(() => {
        if (phoneResendTimer <= 0) return
        const t = setInterval(() => {
            setPhoneResendTimer((prev) => (prev <= 1 ? 0 : prev - 1))
        }, 1000)
        return () => clearInterval(t)
    }, [phoneResendTimer])

    // Sync user data into form state
    useEffect(() => {
        if (user) {
            const placeholderName = identityMissing(user).includes('name')
            setFirstName(placeholderName ? '' : (user.first_name || ''))
            setLastName(placeholderName ? '' : (user.last_name || ''))
            setPhone(user.phone || '')
            setWhatsapp(user.whatsapp || '')
            setSameWhatsApp(user.phone && user.whatsapp && user.phone === user.whatsapp)
            setProfilePhoto(user.profile_picture || '')
        }
    }, [user])

    const closeModal = () => {
        setActiveModal(null)
        setPasswordError('')
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        setEmailDraft('')
        setEmailOtp(['', '', '', '', '', ''])
        setEmailStep('input')
        setEmailMakeDefault(false)
        setEmailError('')
        setEmailResendMessage('')
        setPhoneDraft('')
        setPhoneOtp(['', '', '', '', '', ''])
        setPhoneStep('input')
        setPhoneMakeDefault(false)
        setPhoneError('')
        setPhoneResendMessage('')
        setAddressPlace(null)
        setAddressLabel('')
        setAddressError('')
    }

    // Update profile mutation
    const updateMutation = useMutation({
        mutationFn: (fields) => userService.updateAccount(fields),
        onSuccess: (result) => {
            if (result.success) {
                if (result.token) login(result.token)
                queryClient.invalidateQueries({queryKey: ['user', 'account']})
            }
        },
    })

    // Fill in a missing name through the ordinary account update (POST /account).
    const [nameError, setNameError] = useState('')
    const nameMutation = useMutation({
        mutationFn: ({first, last}) => userService.updateAccount({first_name: first.trim(), last_name: last.trim()}),
        onSuccess: (result) => {
            if (result.success) {
                if (result.token) login(result.token)
                setNameError('')
                queryClient.invalidateQueries({queryKey: ['user', 'account']})
            } else {
                setNameError(result.error || 'Could not save your name.')
            }
        },
    })
    const handleSaveName = () => {
        if (!firstName.trim() || !lastName.trim()) {
            setNameError('Enter your first and last name.')
            return
        }
        setNameError('')
        nameMutation.mutate({first: firstName, last: lastName})
    }

    // Change password mutation
    const passwordMutation = useMutation({
        mutationFn: ({current, next, confirm}) => userService.changePassword(current, next, confirm),
        onSuccess: (result) => {
            if (result.success) {
                if (result.token) login(result.token)
                closeModal()
            } else {
                setPasswordError(result.error)
            }
        },
    })

    // Save photo mutation
    const photoMutation = useMutation({
        mutationFn: (file) => {
            return userService.updateAccount({
                profile_picture: file,
            })
        },
        onSuccess: (result) => {
            if (result.success) {
                if (result.token) login(result.token)
                queryClient.invalidateQueries({queryKey: ['user', 'account']})
                setPreviewPhoto(null)
                setSelectedFile(null)
                closeModal()
            }
        },
    })

    const handleFileChange = (e) => {
        const file = e.target.files?.[0]
        if (file) {
            setSelectedFile(file)
            const reader = new FileReader()
            reader.onload = (ev) => setPreviewPhoto(ev.target.result)
            reader.readAsDataURL(file)
        }
    }

    const handleSavePhoto = () => {
        if (selectedFile) {
            photoMutation.mutate(selectedFile)
        }
    }

    const handleCancelPhoto = () => {
        setPreviewPhoto(null)
        setSelectedFile(null)
        closeModal()
    }

    const handleSaveProfile = () => {
        const fields = {}
        if (sameWhatsApp) fields.whatsapp = phone
        updateMutation.mutate(fields)
    }

    const handleChangePassword = () => {
        setPasswordError('')
        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordError('All fields are required')
            return
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('Passwords do not match')
            return
        }
        passwordMutation.mutate({current: currentPassword, next: newPassword, confirm: confirmPassword})
    }

    const firstFieldError = (raw, fallback) => {
        const validation = raw?.errors
        const first = validation && Object.values(validation)[0]?.[0]
        return first || fallback
    }

    const handleSendEmailCode = async () => {
        setEmailError('')
        const trimmed = (emailDraft || '').trim()
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setEmailError('Please enter a valid email address')
            return
        }
        try {
            const addResult = await addEmailMutation.mutateAsync(trimmed)
            if (!addResult.success) {
                console.error('[add-user-email] failed', addResult)
                const msg = firstFieldError(addResult.raw, addResult.error)
                const alreadyExists = /already|exist/i.test(msg || '')
                if (!alreadyExists) {
                    setEmailError(msg || 'Failed to add email')
                    return
                }
            }
            const otpResult = await requestEmailOtpMutation.mutateAsync(trimmed)
            if (!otpResult.success) {
                console.error('[request-user-email-otp] failed', otpResult)
                setEmailError(firstFieldError(otpResult.raw, otpResult.error) || otpResult.error || 'Failed to send code')
                return
            }
        } catch (err) {
            console.error('[send-email-code] threw', err)
            setEmailError(err?.message || 'Unexpected error while sending code')
            return
        }
        setEmailOtp(['', '', '', '', '', ''])
        setEmailStep('otp')
        setEmailResendTimer(BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS)
    }

    const handleVerifyEmail = async () => {
        setEmailError('')
        const code = emailOtp.join('')
        if (code.length < 6) {
            setEmailError('Please enter a valid 6-digit code')
            return
        }
        try {
            const result = await verifyEmailOtpMutation.mutateAsync({email: emailDraft.trim(), otp: code})
            if (!result.success) {
                console.error('[verify-user-email-otp] failed', result)
                setEmailError(firstFieldError(result.raw, result.error) || result.error || 'Invalid code')
                setEmailOtp(['', '', '', '', '', ''])
                return
            }
            if (emailMakeDefault || emailMustBeDefault) {
                const def = await setDefaultEmailMutation.mutateAsync(emailDraft.trim())
                if (!def.success) {
                    console.error('[set-user-email-default] failed', def)
                    setEmailError(firstFieldError(def.raw, def.error) || def.error || 'Failed to set default')
                    return
                }
            }
        } catch (err) {
            console.error('[verify-email] threw', err)
            setEmailError(err?.message || 'Unexpected error while verifying code')
            return
        }
        closeModal()
    }

    const handleVerifyExistingEmail = async (existingEmail) => {
        setEmailDraft(existingEmail)
        setEmailOtp(['', '', '', '', '', ''])
        setEmailMakeDefault(false)
        setEmailError('')
        setEmailResendMessage('')
        setActiveModal('addEmail')
        try {
            const r = await requestEmailOtpMutation.mutateAsync(existingEmail)
            if (!r.success) {
                console.error('[request-user-email-otp] failed', r)
                setEmailError(firstFieldError(r.raw, r.error) || r.error || 'Failed to send code')
                setEmailStep('input')
                return
            }
        } catch (err) {
            console.error('[verify-existing-email] threw', err)
            setEmailError(err?.message || 'Unexpected error while sending code')
            setEmailStep('input')
            return
        }
        setEmailStep('otp')
        setEmailResendTimer(BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS)
    }

    const handleResendEmailCode = async () => {
        if (emailResendTimer > 0 || requestEmailOtpMutation.isPending) return
        setEmailError('')
        setEmailResendMessage('Sending...')
        try {
            const r = await requestEmailOtpMutation.mutateAsync(emailDraft.trim())
            if (!r.success) {
                console.error('[request-user-email-otp] resend failed', r)
                setEmailError(firstFieldError(r.raw, r.error) || r.error || 'Failed to resend')
                setEmailResendMessage('')
                return
            }
        } catch (err) {
            console.error('[resend-email-code] threw', err)
            setEmailError(err?.message || 'Unexpected error while resending')
            setEmailResendMessage('')
            return
        }
        setEmailResendMessage('Code resent successfully')
        setEmailResendTimer(BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS)
        setTimeout(() => setEmailResendMessage(''), 3000)
    }

    const handleSendPhoneCode = async () => {
        setPhoneError('')
        const parsed = parsePhoneNumberFromString(phoneDraft || '')
        if (!parsed || !parsed.isValid()) {
            setPhoneError('Please enter a valid phone number')
            return
        }
        const phoneNum = parsed.number
        const country = parsed.country?.toLowerCase() || null
        try {
            const addResult = await addPhoneMutation.mutateAsync({phone: phoneNum, countryCode: country})
            if (!addResult.success) {
                console.error('[add-user-phone] failed', addResult)
                const msg = firstFieldError(addResult.raw, addResult.error)
                const alreadyExists = /already|exist/i.test(msg || '')
                if (!alreadyExists) {
                    setPhoneError(msg || 'Failed to add phone')
                    return
                }
            }
            const otpResult = await requestPhoneOtpMutation.mutateAsync(phoneNum)
            if (!otpResult.success) {
                console.error('[request-user-phone-otp] failed', otpResult)
                setPhoneError(firstFieldError(otpResult.raw, otpResult.error) || otpResult.error || 'Failed to send code')
                return
            }
        } catch (err) {
            console.error('[send-phone-code] threw', err)
            setPhoneError(err?.message || 'Unexpected error while sending code')
            return
        }
        setPhoneOtp(['', '', '', '', '', ''])
        setPhoneStep('otp')
        setPhoneResendTimer(BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS)
    }

    const handleVerifyPhone = async () => {
        setPhoneError('')
        const code = phoneOtp.join('')
        if (code.length < 6) {
            setPhoneError('Please enter a valid 6-digit code')
            return
        }
        const parsed = parsePhoneNumberFromString(phoneDraft || '')
        const phoneNum = parsed?.number || phoneDraft
        try {
            const result = await verifyPhoneOtpMutation.mutateAsync({phone: phoneNum, otp: code})
            if (!result.success) {
                console.error('[verify-user-phone-otp] failed', result)
                setPhoneError(firstFieldError(result.raw, result.error) || result.error || 'Invalid code')
                setPhoneOtp(['', '', '', '', '', ''])
                return
            }
            if (phoneMakeDefault) {
                const def = await setDefaultPhoneMutation.mutateAsync(phoneNum)
                if (!def.success) {
                    console.error('[set-user-phone-default] failed', def)
                    setPhoneError(firstFieldError(def.raw, def.error) || def.error || 'Failed to set default')
                    return
                }
            }
        } catch (err) {
            console.error('[verify-phone] threw', err)
            setPhoneError(err?.message || 'Unexpected error while verifying code')
            return
        }
        closeModal()
    }

    const handleVerifyExistingPhone = async (existingPhone) => {
        setPhoneDraft(existingPhone)
        setPhoneOtp(['', '', '', '', '', ''])
        setPhoneMakeDefault(false)
        setPhoneError('')
        setPhoneResendMessage('')
        setActiveModal('addPhone')
        try {
            const r = await requestPhoneOtpMutation.mutateAsync(existingPhone)
            if (!r.success) {
                console.error('[request-user-phone-otp] failed', r)
                setPhoneError(firstFieldError(r.raw, r.error) || r.error || 'Failed to send code')
                setPhoneStep('input')
                return
            }
        } catch (err) {
            console.error('[verify-existing-phone] threw', err)
            setPhoneError(err?.message || 'Unexpected error while sending code')
            setPhoneStep('input')
            return
        }
        setPhoneStep('otp')
        setPhoneResendTimer(BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS)
    }

    const handleResendPhoneCode = async () => {
        if (phoneResendTimer > 0 || requestPhoneOtpMutation.isPending) return
        setPhoneError('')
        setPhoneResendMessage('Sending...')
        const parsed = parsePhoneNumberFromString(phoneDraft || '')
        const phoneNum = parsed?.number || phoneDraft
        try {
            const r = await requestPhoneOtpMutation.mutateAsync(phoneNum)
            if (!r.success) {
                console.error('[request-user-phone-otp] resend failed', r)
                setPhoneError(firstFieldError(r.raw, r.error) || r.error || 'Failed to resend')
                setPhoneResendMessage('')
                return
            }
        } catch (err) {
            console.error('[resend-phone-code] threw', err)
            setPhoneError(err?.message || 'Unexpected error while resending')
            setPhoneResendMessage('')
            return
        }
        setPhoneResendMessage('Code resent successfully')
        setPhoneResendTimer(BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS)
        setTimeout(() => setPhoneResendMessage(''), 3000)
    }

    const handleSaveAddress = async () => {
        setAddressError('')
        if (!addressPlace || !addressPlace.lat || !addressPlace.lng
            || !addressPlace.address || !addressPlace.city
            || !addressPlace.state || !addressPlace.country) {
            setAddressError('Please pick a complete address from the suggestions')
            return
        }
        const payload = {
            label: addressLabel.trim() || null,
            coordinate: {
                lat: addressPlace.lat,
                lng: addressPlace.lng,
                address: addressPlace.address,
                city: addressPlace.city,
                state: addressPlace.state,
                country: addressPlace.country,
            },
        }
        try {
            const result = await addAddressMutation.mutateAsync(payload)
            if (!result.success) {
                console.error('[add-user-address] failed', result)
                setAddressError(firstFieldError(result.raw, result.error) || result.error || 'Failed to save address')
                return
            }
        } catch (err) {
            console.error('[add-address] threw', err)
            setAddressError(err?.message || 'Unexpected error while saving address')
            return
        }
        closeModal()
    }

    const handleCancel = () => {
        if (cancelConfirm) {
            if (user) {
                setFirstName(user.first_name || '')
                setLastName(user.last_name || '')
                setPhone(user.phone || '')
                setWhatsapp(user.whatsapp || '')
            }
            setCancelConfirm(false)
        } else {
            setCancelConfirm(true)
        }
    }

    const avatarUrl = profilePhoto || `https://ui-avatars.com/api/?name=${encodeURIComponent([firstName, lastName].filter(Boolean).join(' ').trim() || 'User')}&background=e0e0e0&color=333&size=120`

    if (userLoading) {
        return (
            <div className="flex flex-col gap-5">
                <h2 className="max-md:text-xl!">Settings</h2>
                <div className="white-global-section">
                    <div className="animate-pulse space-y-6">
                        <div className="h-6 bg-gray-200 rounded w-48"></div>
                        <div className="h-10 bg-gray-200 rounded-full w-full"></div>
                        <div className="h-10 bg-gray-200 rounded-full w-full"></div>
                        <div className="h-10 bg-gray-200 rounded-full w-full"></div>
                        <div className="h-10 bg-gray-200 rounded-full w-full"></div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-5">
            <h2 className="max-md:text-xl!">Settings</h2>

            {/* The same warning the passenger saw on the booking that sent them here, so
                the page they land on states the reason they are on it. The button is off
                because it navigates HERE — see the note in the component. */}
            {profileIdentityMissing.length > 0 && (
                <IdentityIncompleteNotice missing={profileIdentityMissing} showAction={false}/>
            )}

            <div className="white-global-section">
                <h5 className="font-bold mb-10! max-md:mb-5! max-md:text-base!">Personal Information</h5>

                <div className="flex gap-8 max-md:gap-0 max-md:flex-col">
                    {/* Profile Photo */}
                    <div className="shrink-0 block">
                        <div className="relative table m-auto mb-5">
                            <div
                                className="w-30 h-30 rounded-full max-md:w-24 max-md:h-24 bg-gray-200 overflow-hidden border-2 border-gray-100">
                                <img alt="profile photo" src={avatarUrl} className="w-full h-full object-cover"/>
                            </div>
                            <button onClick={() => setActiveModal('editPhoto')}
                                    className="cursor-pointer absolute bottom-0 right-0 w-10 h-10 bg-(--accent-green-light) rounded-full border-2 border-white flex items-center justify-center">
                                <img className="invert" alt="edit" src={pencilIcon}/></button>
                        </div>
                    </div>

                    {/* Form Fields */}
                    <div className="flex-1 flex flex-col gap-5">
                        {/* First Name / Last Name */}
                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label htmlFor="firstName" className="block mb-1 font-bold max-md:text-sm">First Name</label>
                                <input
                                    id="firstName"
                                    type="text"
                                    value={firstName}
                                    disabled={!nameMissing}
                                    onChange={(ev) => setFirstName(ev.target.value)}
                                    className={`rounded-full w-full px-5 py-3.5 max-md:py-2.5 max-md:text-sm outline-none bg-transparent border border-(--bg-dark-grey) ${nameMissing ? '' : 'cursor-not-allowed opacity-60'}`}
                                />
                            </div>
                            <div className="flex-1">
                                <label htmlFor="lastName" className="block mb-1 font-bold max-md:text-sm">Last Name</label>
                                <input
                                    id="lastName"
                                    type="text"
                                    value={lastName}
                                    disabled={!nameMissing}
                                    onChange={(ev) => setLastName(ev.target.value)}
                                    className={`rounded-full w-full px-5 py-3.5 max-md:py-2.5 max-md:text-sm outline-none bg-transparent border border-(--bg-dark-grey) ${nameMissing ? '' : 'cursor-not-allowed opacity-60'}`}
                                />
                            </div>
                        </div>
                        {nameMissing && (
                            <div className="-mt-2">
                                {nameError && <p className="text-red-500 text-sm mb-2!">{nameError}</p>}
                                <button type="button" onClick={handleSaveName} disabled={nameMutation.isPending}
                                        className="cursor-pointer flex items-center justify-center rounded-full bg-(--bg-dark) text-white font-medium px-8 py-3 disabled:opacity-60">
                                    {nameMutation.isPending ? <Loader color="#fff"/> : 'Save name'}
                                </button>
                            </div>
                        )}

                        {/* Email Addresses */}
                        <div>
                            <label className="block mb-1 font-bold max-md:text-sm">Email Addresses</label>
                            {emailsLoading ? (
                                <div className="animate-pulse h-12 bg-gray-100 rounded-full"/>
                            ) : (() => {
                                const defaultEmail = emails.find((e) => e.is_default)
                                const otherEmails = emails.filter((e) => !e.is_default)
                                return (
                                    <div className="flex flex-col gap-2">
                                        {emails.length === 0 && (
                                            <p className="text-(--text-secondary) mb-0!">No emails on file yet.</p>
                                        )}
                                        {defaultEmail && (
                                            <div key={defaultEmail.id} className="flex items-center justify-between gap-2 rounded-full w-full pl-5 pr-2 py-2 bg-transparent border border-(--bg-dark-grey)">
                                                <span className="flex-1 truncate max-md:text-sm">{defaultEmail.email}</span>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    {!defaultEmail.is_verified && (
                                                        <button
                                                            onClick={() => handleVerifyExistingEmail(defaultEmail.email)}
                                                            disabled={requestEmailOtpMutation.isPending}
                                                            className="cursor-pointer bg-amber-100 text-amber-800 font-medium px-6 py-1 rounded-full text-xs max-md:px-3 disabled:opacity-50">
                                                            Unverified
                                                        </button>
                                                    )}
                                                    {defaultEmail.is_verified && (
                                                        <>
                                                            <img alt="" src={lock} className="w-4 h-4"/>
                                                            <span className="bg-(--bg-grey) text-(--text-primary) text-sm font-medium px-6 py-1 rounded-full min-w-20 h-7 max-md:px-3 max-md:text-xs max-md:min-w-0 inline-flex items-center justify-center">Default</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {otherEmails.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {otherEmails.map((e) => (
                                                    <div key={e.id} className="flex items-center gap-2 rounded-full pl-5 pr-2 py-2 bg-transparent border border-(--bg-dark-grey)">
                                                        <span className="truncate max-w-[220px]">{e.email}</span>
                                                        {!e.is_verified && (
                                                            <button
                                                                onClick={() => handleVerifyExistingEmail(e.email)}
                                                                disabled={requestEmailOtpMutation.isPending}
                                                                className="cursor-pointer bg-amber-100 text-amber-800 font-medium px-6 py-1 rounded-full text-xs max-md:px-3 disabled:opacity-50">
                                                                Unverified
                                                            </button>
                                                        )}
                                                        {e.is_verified && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setDefaultEmailMutation.mutate(e.email)}
                                                                disabled={setDefaultEmailMutation.isPending && setDefaultEmailMutation.variables === e.email}
                                                                className="cursor-pointer bg-(--bg-grey) text-(--text-primary) text-sm font-medium px-6 py-1 rounded-full min-w-20 h-7 max-md:px-3 max-md:text-xs max-md:min-w-0 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-default">
                                                                {setDefaultEmailMutation.isPending && setDefaultEmailMutation.variables === e.email ? <Loader color="#000"/> : 'Default'}
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )
                            })()}
                            <button onClick={() => setActiveModal('addEmail')} className="cursor-pointer mt-3 flex items-center gap-2 text-white font-medium pr-12 pl-6 py-3 rounded-full bg-(--bg-dark) bg-[url('/add-circle-icon.svg')] bg-size-[28px_28px] bg-no-repeat bg-position-[right_8px_center]">
                                Add New Email
                            </button>
                        </div>

                        {/* Mobile Numbers */}
                        <div>
                            <label className="block mb-1 font-bold max-md:text-sm">Mobile Numbers</label>
                            {phonesLoading ? (
                                <div className="animate-pulse h-12 bg-gray-100 rounded-full"/>
                            ) : (() => {
                                const defaultPhone = phones.find((p) => p.is_default && p.is_verified) ?? phones.find((p) => p.is_verified)
                                const otherPhones = phones.filter((p) => p.id !== defaultPhone?.id)
                                return (
                                    <div className="flex flex-col gap-2">
                                        {phones.length === 0 && (
                                            <p className="text-(--text-secondary) mb-0!">No phone numbers on file yet.</p>
                                        )}
                                        {defaultPhone && (
                                            <>
                                                <div key={defaultPhone.id} className="flex items-center justify-between gap-2 rounded-full w-full pl-5 pr-2 py-2 bg-transparent border border-(--bg-dark-grey)">
                                                    <span className="flex-1 truncate max-md:text-sm">{defaultPhone.phone}</span>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        {!defaultPhone.is_verified && (
                                                            <button
                                                                onClick={() => handleVerifyExistingPhone(defaultPhone.phone)}
                                                                disabled={requestPhoneOtpMutation.isPending}
                                                                className="cursor-pointer bg-amber-100 text-amber-800 font-medium px-6 py-1 rounded-full text-xs max-md:px-3 disabled:opacity-50">
                                                                Unverified
                                                            </button>
                                                        )}
                                                        {defaultPhone.is_verified && (
                                                            <>
                                                                <img alt="" src={lock} className="w-4 h-4"/>
                                                                <span className="bg-(--bg-grey) text-(--text-primary) text-sm font-medium px-6 py-1 rounded-full min-w-20 h-7 max-md:px-3 max-md:text-xs max-md:min-w-0 inline-flex items-center justify-center">Default</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <label htmlFor="sameWhatsApp" className="flex items-center gap-2 cursor-pointer">
                                                    <RadioButton
                                                        id="sameWhatsApp"
                                                        type="checkbox"
                                                        checked={sameWhatsApp}
                                                        onChange={(e) => setSameWhatsApp(e.target.checked)}
                                                    />
                                                    <span>Same no on WhatsApp?</span>
                                                </label>
                                            </>
                                        )}
                                        {otherPhones.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {otherPhones.map((p) => (
                                                    <div key={p.id} className="flex items-center gap-2 rounded-full pl-5 pr-2 py-2 bg-transparent border border-(--bg-dark-grey)">
                                                        <span className="truncate max-w-[220px]">{p.phone}</span>
                                                        {!p.is_verified && (
                                                            <button
                                                                onClick={() => handleVerifyExistingPhone(p.phone)}
                                                                disabled={requestPhoneOtpMutation.isPending}
                                                                className="cursor-pointer bg-amber-100 text-amber-800 font-medium px-6 py-1 rounded-full text-xs max-md:px-3 disabled:opacity-50">
                                                                Unverified
                                                            </button>
                                                        )}
                                                        {p.is_verified && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setDefaultPhoneMutation.mutate(p.phone)}
                                                                disabled={setDefaultPhoneMutation.isPending && setDefaultPhoneMutation.variables === p.phone}
                                                                className="cursor-pointer bg-(--bg-grey) text-(--text-primary) text-sm font-medium px-6 py-1 rounded-full min-w-20 h-7 max-md:px-3 max-md:text-xs max-md:min-w-0 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-default">
                                                                {setDefaultPhoneMutation.isPending && setDefaultPhoneMutation.variables === p.phone ? <Loader color="#000"/> : 'Default'}
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )
                            })()}
                            <button onClick={() => setActiveModal('addPhone')} className="cursor-pointer mt-3 flex items-center gap-2 text-white font-medium pr-12 pl-6 py-3 rounded-full bg-(--bg-dark) bg-[url('/add-circle-icon.svg')] bg-size-[28px_28px] bg-no-repeat bg-position-[right_8px_center]">
                                Add New Mobile Number
                            </button>
                        </div>

                        {/* Pickup Addresses */}
                        <div>
                            <label className="font-bold block mb-1">Your pickup addresses</label>
                            {addresses.length > 0 && (
                                <div className="flex flex-col gap-2 mb-3">
                                    {addresses.map((a) => {
                                        let coord = null
                                        if (a.coordinate) {
                                            if (typeof a.coordinate === 'object') coord = a.coordinate
                                            else { try { coord = JSON.parse(a.coordinate) } catch { coord = null } }
                                        }
                                        return (
                                            <div key={a.id} className="flex items-center gap-3 rounded-full w-full px-5 py-2 bg-transparent border border-(--bg-dark-grey)">
                                                <img alt="" src={locationIcon} className="shrink-0"/>
                                                <span className="flex-1 truncate text-(--text-secondary)">{coord?.address || ''}</span>
                                                {a.label && <span className="font-medium shrink-0">{a.label}</span>}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                            <button onClick={() => setActiveModal('addAddress')} className="cursor-pointer flex items-center gap-2 text-white font-medium pr-12 pl-6 py-3 rounded-full bg-(--bg-dark) bg-[url('/add-circle-icon.svg')] bg-size-[28px_28px] bg-no-repeat bg-position-[right_8px_center]">
                                Add pickup address
                            </button>
                        </div>

                        {/* Password */}
                        {/*<div>*/}
                        {/*    <label htmlFor="password" className="block mb-1 font-bold max-md:text-sm">Password</label>*/}
                        {/*    <div className="flex items-center justify-between rounded-full w-full pl-5 pr-1 py-1 bg-transparent border border-(--bg-dark-grey)">*/}
                        {/*        <div className="flex items-center gap-2">*/}
                        {/*            xxxxxxxxxx*/}
                        {/*        </div>*/}
                        {/*        <button onClick={() => setActiveModal('changePassword')}*/}
                        {/*            className="flex items-center justify-center gap-2 cursor-pointer bg-(--bg-grey) font-medium border border-(--bg-dark-grey) rounded-full px-5 py-2.5 whitespace-nowrap text-(--text-primary)">*/}
                        {/*            <img alt="lock" src={lock}/> Change Password*/}
                        {/*        </button>*/}
                        {/*    </div>*/}
                        {/*</div>*/}

                        {/* Language */}
                        {/*<div>*/}
                        {/*    <label htmlFor="language" className="block mb-1 font-bold max-md:text-sm">Language</label>*/}
                        {/*    <div className="flex items-center justify-between rounded-full w-full px-5 py-3.5 bg-transparent border border-(--bg-dark-grey)">*/}
                        {/*        <div className="flex items-center gap-2">*/}
                        {/*            {selectedLanguage}*/}
                        {/*        </div>*/}
                        {/*        <button onClick={() => setActiveModal('editLanguage')} className="cursor-pointer shrink-0">*/}
                        {/*            <img className="invert" src={pencilIcon} alt="edit"/>*/}
                        {/*        </button>*/}
                        {/*    </div>*/}
                        {/*</div>*/}
                    </div>
                </div>

            </div>

            {/* ── PRIVACY AND LEGAL ─────────────────────────────────────────
                ⚠️ LAST ON THE PAGE, AND ON ITS OWN. Everything above changes something that
                can be changed back. This cannot — not by the passenger, not by support, not
                by an admin — so it does not belong in a list of ordinary settings where a
                mis-tap lands. The app stores require the path to exist and to be findable;
                nothing requires it to be convenient. */}
            <div className="white-global-section flex flex-col gap-4">
                <h5 className="mb-0! font-bold max-md:text-base!">Privacy and Legal</h5>
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <p className="mb-0! max-w-xl text-(--text-secondary) max-md:text-sm">
                        Deleting your account signs you out and cannot be undone. Any upcoming
                        ride is cancelled free of charge and unused credit is forfeited.
                    </p>
                    <button
                        type="button"
                        onClick={() => setActiveModal('deleteAccount')}
                        className="min-h-12 max-md:min-h-10 max-md:text-sm cursor-pointer rounded-full border border-(--text-red) bg-transparent px-6 font-semibold text-(--text-red)"
                    >
                        Delete Account
                    </button>
                </div>
            </div>

            {/* ⚠️ THE CONTACT IS THE ACCOUNT'S OWN AND IS NOT EDITABLE HERE — see
                `DeleteAccountFlow`. Signed in, an open field would let someone request a
                deletion code for somebody ELSE'S account from inside their own settings.

                ⚠️ AND `logout` RUNS ONLY AFTER THE SERVER HAS CONFIRMED. Every token is
                already revoked by then, so the next authenticated call would 401 and bounce
                them anyway — doing it here just makes that orderly instead of abrupt. */}
            <Modal isOpen={activeModal === 'deleteAccount'} onClose={closeModal} className="m-4">
                <ModalHeader title="Delete Account" onClose={closeModal}/>
                <DeleteAccountFlow
                    leg="passenger"
                    defaultContact={visibleEmail(user?.email) || user?.phone || ''}
                    onCancel={closeModal}
                    onDeleted={() => { closeModal(); logout() }}
                />
            </Modal>

            {/* Edit Profile Photo Modal */}
            <Modal isOpen={activeModal === 'editPhoto'} onClose={handleCancelPhoto} className="m-4"
                   footer={
                       <button onClick={handleSavePhoto} disabled={photoMutation.isPending || !selectedFile} className="cursor-pointer w-full flex items-center justify-center rounded-full bg-(--bg-dark) text-white font-medium py-4 relative">
                           {photoMutation.isPending ? <Loader color="#fff"/> : 'Save Changes'}
                           <span className="absolute right-2 w-10 h-10 bg-(--bg-grey) rounded-full flex items-center justify-center">
                               <img alt="" src={rightArrow}/>
                           </span>
                       </button>
                   }>
                    <ModalHeader title="Edit Profile Photo" onClose={handleCancelPhoto} titleIcon={<img className="w-5" alt="" src={lock}/>}/>

                    <div className="flex flex-col items-center gap-5">
                        <div className="w-32 h-32 rounded-full bg-gray-200 overflow-hidden border-2 border-gray-100">
                            <img
                                alt="preview"
                                src={previewPhoto || avatarUrl}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="hidden"
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="cursor-pointer flex items-center gap-2 bg-(--bg-dark) text-white font-medium px-6 py-2.5 rounded-full"
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 2v8M4 6l4-4 4 4" stroke="white" strokeWidth="1.5" strokeLinecap="round"
                                      strokeLinejoin="round"/>
                                <path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="white" strokeWidth="1.5"
                                      strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Choose Photo
                        </button>
                    </div>
            </Modal>

            {/* Add New Email Modal */}
            <Modal isOpen={activeModal === 'addEmail'} onClose={closeModal} className="m-4"
                   footer={
                       emailStep === 'input' ? (
                           <button
                               onClick={handleSendEmailCode}
                               disabled={addEmailMutation.isPending || requestEmailOtpMutation.isPending}
                               className="cursor-pointer w-full flex items-center justify-center rounded-full bg-(--bg-dark) text-white font-medium py-4 relative">
                               {(addEmailMutation.isPending || requestEmailOtpMutation.isPending) ? <Loader color="#fff"/> : 'Send Verification Code'}
                               <span className="absolute right-2 w-10 h-10 bg-(--bg-grey) rounded-full flex items-center justify-center">
                                   <img alt="" src={rightArrow}/>
                               </span>
                           </button>
                       ) : (
                           <>
                               <button
                                   onClick={handleVerifyEmail}
                                   disabled={verifyEmailOtpMutation.isPending || setDefaultEmailMutation.isPending}
                                   className="cursor-pointer w-full flex items-center justify-center rounded-full bg-(--bg-dark) text-white font-medium py-4 relative mb-3">
                                   {(verifyEmailOtpMutation.isPending || setDefaultEmailMutation.isPending) ? <Loader color="#fff"/> : 'Verify Email'}
                                   <span className="absolute right-2 w-10 h-10 bg-(--bg-grey) rounded-full flex items-center justify-center">
                                       <img alt="" src={rightArrow}/>
                                   </span>
                               </button>
                               <div className="flex items-center justify-center gap-3">
                                   <button
                                       onClick={handleResendEmailCode}
                                       disabled={emailResendTimer > 0 || requestEmailOtpMutation.isPending}
                                       className={`text-sm font-medium ${emailResendTimer > 0 || requestEmailOtpMutation.isPending ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer text-(--text-primary) hover:opacity-80'}`}>
                                       Resend code
                                   </button>
                                   {emailResendTimer > 0 && (
                                       <span className="text-sm text-(--text-secondary)">{formatOtpTimer(emailResendTimer)}</span>
                                   )}
                               </div>
                               {emailResendMessage && (
                                   <p className="text-sm text-center text-(--text-secondary) mt-2 mb-0!">{emailResendMessage}</p>
                               )}
                           </>
                       )
                   }>
                <ModalHeader title={emailStep === 'input' ? 'Add New Email' : 'Verify Email'} onClose={closeModal}/>
                {emailStep === 'input' ? (
                    <>
                        <input
                            type="email"
                            value={emailDraft}
                            onChange={(e) => { setEmailDraft(e.target.value); if (emailError) setEmailError('') }}
                            placeholder="name@example.com"
                            className="rounded-full w-full px-5 py-3.5 max-md:py-2.5 max-md:text-sm outline-none bg-transparent border border-(--bg-dark-grey)"/>
                        <div className="flex justify-end mt-3">
                            <label className={`flex items-center gap-2 ${emailMustBeDefault ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                <RadioButton
                                    type="checkbox"
                                    checked={emailMakeDefault || emailMustBeDefault}
                                    disabled={emailMustBeDefault}
                                    onChange={(e) => setEmailMakeDefault(e.target.checked)}/>
                                <span>Set as default</span>
                            </label>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="text-(--text-secondary) text-center">We sent a code to <span className="font-bold text-(--text-primary)">{emailDraft}</span></p>
                        <OtpInput
                            value={emailOtp}
                            onChange={(next) => { setEmailOtp(next); if (emailError) setEmailError('') }}
                            autoFocus
                            className="my-4"
                        />
                    </>
                )}
                {emailError && <p className="text-red-500 mt-3 mb-0! text-center">{emailError}</p>}
            </Modal>

            {/* Add New Mobile Number Modal */}
            <Modal isOpen={activeModal === 'addPhone'} onClose={closeModal} className="m-4"
                   footer={
                       phoneStep === 'input' ? (
                           <>
                               <button
                                   onClick={handleSendPhoneCode}
                                   disabled={addPhoneMutation.isPending || requestPhoneOtpMutation.isPending}
                                   className="cursor-pointer w-full flex items-center justify-center rounded-full bg-(--bg-dark) text-white font-medium py-4 relative mb-3">
                                   {(addPhoneMutation.isPending || requestPhoneOtpMutation.isPending) ? <Loader color="#fff"/> : 'Send Verification Code'}
                                   <span className="absolute right-2 w-10 h-10 bg-(--bg-grey) rounded-full flex items-center justify-center">
                                       <img alt="" src={rightArrow}/>
                                   </span>
                               </button>
                               <button onClick={closeModal} className="cursor-pointer w-full text-center font-medium">Cancel</button>
                           </>
                       ) : (
                           <>
                               <button
                                   onClick={handleVerifyPhone}
                                   disabled={verifyPhoneOtpMutation.isPending || setDefaultPhoneMutation.isPending}
                                   className="cursor-pointer w-full flex items-center justify-center rounded-full bg-(--bg-dark) text-white font-medium py-4 relative mb-3">
                                   {(verifyPhoneOtpMutation.isPending || setDefaultPhoneMutation.isPending) ? <Loader color="#fff"/> : 'Verify Number'}
                                   <span className="absolute right-2 w-10 h-10 bg-(--bg-grey) rounded-full flex items-center justify-center">
                                       <img alt="" src={rightArrow}/>
                                   </span>
                               </button>
                               <div className="flex items-center justify-center gap-3">
                                   <button
                                       onClick={handleResendPhoneCode}
                                       disabled={phoneResendTimer > 0 || requestPhoneOtpMutation.isPending}
                                       className={`text-sm font-medium ${phoneResendTimer > 0 || requestPhoneOtpMutation.isPending ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer text-(--text-primary) hover:opacity-80'}`}>
                                       Resend code
                                   </button>
                                   {phoneResendTimer > 0 && (
                                       <span className="text-sm text-(--text-secondary)">{formatOtpTimer(phoneResendTimer)}</span>
                                   )}
                               </div>
                               {phoneResendMessage && (
                                   <p className="text-sm text-center text-(--text-secondary) mt-2 mb-0!">{phoneResendMessage}</p>
                               )}
                           </>
                       )
                   }>
                <ModalHeader title={phoneStep === 'input' ? 'Add New Mobile Number' : 'Verify Mobile Number'} onClose={closeModal}/>
                {phoneStep === 'input' ? (
                    <>
                        <FlagSelectWithNumber
                            defaultCountry="ca"
                            value={phoneDraft}
                            onChange={(v) => { setPhoneDraft(v); if (phoneError) setPhoneError('') }}
                            className="px-2 py-2"
                        />
                        <div className="flex justify-end mt-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <RadioButton
                                    type="checkbox"
                                    checked={phoneMakeDefault}
                                    onChange={(e) => setPhoneMakeDefault(e.target.checked)}/>
                                <span>Set as default</span>
                            </label>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="text-(--text-secondary) text-center">We sent a code to <span className="font-bold text-(--text-primary)">{phoneDraft}</span></p>
                        <OtpInput
                            value={phoneOtp}
                            onChange={(next) => { setPhoneOtp(next); if (phoneError) setPhoneError('') }}
                            autoFocus
                            className="my-4"
                        />
                    </>
                )}
                {phoneError && <p className="text-red-500 mt-3 mb-0! text-center">{phoneError}</p>}
            </Modal>

            {/* Add Pickup Address Modal */}
            <Modal isOpen={activeModal === 'addAddress'} onClose={closeModal} className="m-4"
                   footer={
                       <>
                           <button
                               onClick={handleSaveAddress}
                               disabled={addAddressMutation.isPending}
                               className="cursor-pointer w-full flex items-center justify-center rounded-full bg-(--bg-dark) text-white font-medium py-4 relative mb-3">
                               {addAddressMutation.isPending ? <Loader color="#fff"/> : 'Save Address'}
                               <span className="absolute right-2 w-10 h-10 bg-(--accent-green-light) rounded-full flex items-center justify-center">
                                   <img alt="" src={rightArrow}/>
                               </span>
                           </button>
                           <button onClick={closeModal} className="cursor-pointer w-full text-center font-medium">Cancel</button>
                       </>
                   }>
                <ModalHeader title="Add New Address" onClose={closeModal}/>
                <div>
                    <label className="block mb-1 font-bold max-md:text-sm">Search address</label>
                    <div className="flex items-center rounded-full w-full px-5 py-3.5 border border-(--bg-dark-grey)">
                        <img alt="" src={locationIcon} className="shrink-0 mr-2"/>
                        <PlaceAutocomplete
                            id="edit-profile-address"
                            placeholder="Start typing an address..."
                            value={addressPlace?.address || ''}
                            onChange={() => { if (addressError) setAddressError('') }}
                            onPlaceSelected={(place) => { setAddressPlace(place); setAddressError('') }}
                            className="flex-1 outline-none bg-transparent mb-0!"
                        />
                    </div>
                </div>
                <div className="mt-3">
                    <label className="block mb-1 font-bold text-sm">Label (optional)</label>
                    <input
                        type="text"
                        value={addressLabel}
                        onChange={(e) => setAddressLabel(e.target.value)}
                        placeholder="e.g. Home, Work"
                        className="rounded-full w-full px-5 py-3 outline-none bg-transparent border border-(--bg-dark-grey)"/>
                </div>
                {addressError && <p className="text-red-500 mt-3 mb-0!">{addressError}</p>}
            </Modal>

            {/* Change Password Modal */}
            <Modal isOpen={activeModal === 'changePassword'} onClose={closeModal} className="m-4"
                   footer={
                       <button onClick={handleChangePassword} disabled={passwordMutation.isPending} className="cursor-pointer w-full flex items-center justify-center rounded-full bg-(--bg-dark) text-white font-medium py-4 relative">
                           {passwordMutation.isPending ? <Loader color="#fff"/> : 'Change Password'}
                           <span className="absolute right-2 w-10 h-10 bg-(--bg-grey) rounded-full flex items-center justify-center">
                               <img alt="" src={rightArrow}/>
                           </span>
                       </button>
                   }>
                <ModalHeader title="Change Password" onClose={closeModal} titleIcon={<img className="w-5" alt="" src={lock}/>}/>
                <div>
                    <label className="block mb-1 font-bold max-md:text-sm">Current password</label>
                    <div className="flex items-center rounded-full w-full px-5 py-3.5 border border-(--bg-dark-grey)">
                        <input type={showCurrentPass ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" className="flex-1 outline-none bg-transparent"/>
                        <button type="button" onClick={() => setShowCurrentPass(!showCurrentPass)} className="cursor-pointer shrink-0 text-(--text-secondary)">
                            {showCurrentPass ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" stroke="currentColor" strokeWidth="1.5"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/></svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            )}
                        </button>
                    </div>
                </div>
                <div className="mt-4">
                    <label className="block mb-1 font-bold max-md:text-sm">New password</label>
                    <div className="flex items-center rounded-full w-full px-5 py-3.5 border border-(--bg-dark-grey)">
                        <input type={showNewPass ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" className="flex-1 outline-none bg-transparent"/>
                        <button type="button" onClick={() => setShowNewPass(!showNewPass)} className="cursor-pointer shrink-0 text-(--text-secondary)">
                            {showNewPass ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" stroke="currentColor" strokeWidth="1.5"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/></svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            )}
                        </button>
                    </div>
                </div>
                <div className="mt-4">
                    <label className="block mb-1 font-bold max-md:text-sm">Confirm new password</label>
                    <div className="flex items-center rounded-full w-full px-5 py-3.5 border border-(--bg-dark-grey)">
                        <input type={showConfirmPass ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="flex-1 outline-none bg-transparent"/>
                        <button type="button" onClick={() => setShowConfirmPass(!showConfirmPass)} className="cursor-pointer shrink-0 text-(--text-secondary)">
                            {showConfirmPass ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" stroke="currentColor" strokeWidth="1.5"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/></svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            )}
                        </button>
                    </div>
                </div>
                {passwordError && <p className="text-red-500 mt-3 mb-0!">{passwordError}</p>}
                {passwordMutation.isSuccess && !passwordMutation.data?.success && (
                    <p className="text-red-500 mt-3 mb-0!">{passwordMutation.data?.error}</p>
                )}
            </Modal>

            {/* Edit Language Modal */}
            {/*<Modal isOpen={activeModal === 'editLanguage'} onClose={closeModal} className="m-4"*/}
            {/*       footer={*/}
            {/*           <>*/}
            {/*               <button className="cursor-pointer w-full flex items-center justify-center rounded-full bg-(--bg-dark) text-white font-medium py-4 relative mb-3">*/}
            {/*                   Save Language*/}
            {/*                   <span className="absolute right-2 w-10 h-10 bg-(--bg-grey) rounded-full flex items-center justify-center">*/}
            {/*                       <img alt="" src={rightArrow}/>*/}
            {/*                   </span>*/}
            {/*               </button>*/}
            {/*               <button onClick={closeModal} className="cursor-pointer w-full text-center font-medium">Cancel</button>*/}
            {/*           </>*/}
            {/*       }>*/}
            {/*    <ModalHeader title="Edit Language" onClose={closeModal}/>*/}
            {/*    <div>*/}
            {/*        <label className="block mb-1 font-bold max-md:text-sm">Language</label>*/}
            {/*        <Listbox value={selectedLanguage} onChange={setSelectedLanguage}>*/}
            {/*            <ListboxButton className="rounded-full w-full px-5 py-3.5 max-md:py-2.5 max-md:text-sm outline-none bg-transparent border border-(--bg-dark-grey) cursor-pointer text-left flex items-center justify-between">*/}
            {/*                {selectedLanguage}*/}
            {/*                <svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>*/}
            {/*            </ListboxButton>*/}
            {/*            <ListboxOptions anchor="bottom" className="w-[var(--button-width)] mt-1 rounded-2xl bg-white border border-(--bg-dark-grey) shadow-lg overflow-hidden z-[999999]">*/}
            {/*                {languages.map((lang) => (*/}
            {/*                    <ListboxOption key={lang} value={lang} className="px-5 py-3 cursor-pointer data-[focus]:bg-(--bg-grey) data-[selected]:font-bold">*/}
            {/*                        {lang}*/}
            {/*                    </ListboxOption>*/}
            {/*                ))}*/}
            {/*            </ListboxOptions>*/}
            {/*        </Listbox>*/}
            {/*    </div>*/}
            {/*</Modal>*/}

        </div>
    )
}
